import { GroupedInsights } from '@toggle/chart';
import {
  isCatalystInsight,
  isEarningsInsight,
  isInsightFilter,
} from '@toggle/toggle';
import { isToday } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { ScenarioIconWrapper } from '~/views/dashboard/my-assets-widget/assets-table/insights-cell/insights-icon/InsightIcon.styles';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import {
  InsightIconsRoot,
  InsightIconsRootProps,
} from '~/views/insights/components/insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '~/views/insights/components/insights-tooltip/InsightsTooltip';
import { CatalystInsightIcon } from '~/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';

import * as S from './ChartInsightIcon.styles';
import { calculateIconPosition } from './utils';

interface ChartInsightIconProps {
  groupedInsights: GroupedInsights;
  insightIconRight: number;
  insightIconTop: number;
  yOffsetCount: number;
  minIconY?: number;
  isSimpleChart?: boolean;
  source: PosthogTrackingSource;
}

export const ChartInsightIcon = ({
  groupedInsights,
  insightIconTop,
  insightIconRight,
  yOffsetCount,
  minIconY,
  isSimpleChart,
  source,
}: ChartInsightIconProps) => {
  const { t } = useTranslation(['chart']);
  const { openDrawer: openInsightDrawer } = useInsightDrawer();

  const singleIconWidth = isSimpleChart ? 12 : 24;
  const singleIconHeight = isSimpleChart ? 14 : 28;

  const verticalCombinedWidth = isSimpleChart ? 12 : 24;
  const verticalCombinedHeight = isSimpleChart ? 24 : 47;

  return (
    <>
      {groupedInsights.insights.map((insight, idx) => {
        if (isCatalystInsight(insight)) {
          const { iconY, iconRight } = calculateIconPosition({
            idx,
            iconHeight: singleIconHeight,
            iconWidth: singleIconWidth,
            insightIconTop,
            insightIconRight,
            yOffsetCount,
            minIconY,
          });

          return (
            <S.IconContainer key={insight.id} $top={iconY} $right={iconRight}>
              <CatalystInsightIcon
                catalystInsightId={insight.id}
                subCategory={insight.sub_category}
                customSize={{
                  width: singleIconWidth,
                  height: singleIconHeight,
                }}
                isBordered
                identifier={insight.identifier}
                source={source}
                category={insight.category}
              />
            </S.IconContainer>
          );
        }

        if (isEarningsInsight(insight)) {
          const { iconY, iconRight } = calculateIconPosition({
            idx,
            iconHeight: singleIconHeight,
            iconWidth: singleIconWidth,
            insightIconTop,
            insightIconRight,
            yOffsetCount,
            minIconY,
          });

          return (
            <S.IconContainer key={insight.id} $top={iconY} $right={iconRight}>
              <EarningsInsightIcon
                entityTag={insight.entity_tag}
                earningsInsight={insight}
                customSize={{
                  width: singleIconWidth,
                  height: singleIconHeight,
                }}
                isBordered
                source={source}
              />
            </S.IconContainer>
          );
        }

        if (!isInsightFilter(insight)) {
          return null;
        }

        const isStarred = isToday(new Date(insight.created_at));
        const conditions = getConditionIcons({
          kind: insight.metadata.kind,
          conditions: insight.metadata.conditions,
        });
        const isCombined = conditions.length > 1;
        const iconWidth = isCombined ? verticalCombinedWidth : singleIconWidth;
        const iconHeight = isCombined
          ? verticalCombinedHeight
          : singleIconHeight;

        const { iconY, iconRight } = calculateIconPosition({
          idx,
          iconHeight,
          iconWidth,
          insightIconTop,
          insightIconRight,
          yOffsetCount,
          minIconY,
        });

        const iconSize = {
          width: `${iconWidth}px`,
          height: `${iconHeight}px`,
        };

        const episodeLabel = t('chart:insightCard.episode', {
          count: insight.metadata.episode_count,
        });

        const handleScenarioClick = () => {
          openInsightDrawer({
            id: insight.id,
            trackingProperties: { source },
          });
        };

        const insightTooltipProps: InsightIconsRootProps = {
          conditions,
          direction: getExploreDirection(insight.metadata.direction),
          customSize: iconSize,
          isStarred,
          isCombined,
          layout: 'vertical',
          isSparklesCondensed: isSimpleChart,
        };

        return (
          <InsightsTooltip
            key={insight.id}
            horizon={insight.metadata.horizon}
            median={insight.metadata.median_return}
            stars={insight.metadata.stars}
            name={insight.card.name}
            entityTag={insight.metadata.entity}
            episodeLabel={episodeLabel}
            {...insightTooltipProps}
          >
            <S.IconContainer key={insight.id} $top={iconY} $right={iconRight}>
              <ScenarioIconWrapper>
                <InsightIconsRoot {...insightTooltipProps} onIconClick={handleScenarioClick} source={source} />
              </ScenarioIconWrapper>
            </S.IconContainer>
          </InsightsTooltip>
        );
      })}
    </>
  );
};
